/**
 * @fileOverview This wraps the city plan view in a global filter that passes
 * the filter props through
 */

import React from 'react'
import FixedGlobalSearchWithURL, {
  getTagsFromSearchQuery,
} from '../../Components/FixedGlobalSearch/FixedGlobalSearchWithUrl'
import {
  dropTimeZoneFromDate,
  getWeekNumberAndYear,
} from '../../shared/libraries/utils/date'
import { normalizeGraphQLConnection } from '../../shared/graphql/utils'

const CityPlanFilterWrapper = props => {
  const { children, campaignResults, location, history, locationData } = props
  const tagMapping = formatData(campaignResults, locationData)
  const selectedTags = getTagsFromSearchQuery(location.search)

  // Get all the suggestions from the tag mapping
  var suggestions = []

  Object.keys(tagMapping).forEach(key => {
    Object.values(tagMapping[key]).forEach(tag => {
      suggestions.push(tag)
    })
  })

  var filterState = convertTagsToFilterState(selectedTags, tagMapping)
  // Prune our filter state, if we have no values in a category we don't want
  // to filter by it
  Object.keys(filterState).forEach(key => {
    if (filterState[key].length == 0) delete filterState[key]
  })

  var child = React.cloneElement(children, {
    filterState,
  })
  // Get everything we need from state

  return (
    <div>
      <FixedGlobalSearchWithURL
        suggestions={suggestions}
        location={location}
        onLocationChange={newLoc => history.push(newLoc)}
      />
      {child}
    </div>
  )
}

const formatData = (campaignData, locationData) => {
  if (!campaignData)
    return {
      tagMapping: {},
    }

  const tagMapping = {
    brandAmbassadors: {},
    locations: {},
    weeks: {},
    postcodes: {},
  }

  const missions = campaignData.cityPlans.nodes.reduce(
    (acc, cityPlan) => [
      ...acc,
      ...normalizeGraphQLConnection(cityPlan.missions),
    ],
    [],
  )

  const weekNumbersByYear = {}

  missions.forEach(mission => {
    if (mission.location) {
      tagMapping.locations[mission.location.id] = mission.location.name
    }
    if (mission.user) {
      tagMapping.brandAmbassadors[mission.user.id] = `${
        mission.user.firstName
      } ${mission.user.lastName}`
    }

    const missionDate = new Date(dropTimeZoneFromDate(mission.dateTime))
    const { weekNumber, year } = getWeekNumberAndYear(missionDate)
    weekNumbersByYear[year] = weekNumbersByYear[year] || {}
    weekNumbersByYear[year][weekNumber] =
      weekNumbersByYear[year][weekNumber] || true
  })

  var singleYear = Object.keys(weekNumbersByYear).length < 2
  Object.keys(weekNumbersByYear).forEach(year => {
    Object.keys(weekNumbersByYear[year]).forEach(weekNumber => {
      // So if we have a week number that has an ambigious year we make all the
      // tags have the format of WEEK-YEAR.
      tagMapping['weeks'][year + '-' + weekNumber] = singleYear
        ? 'W' + weekNumber
        : 'W' + weekNumber + '-' + year
    })
  })

  // Add the time of day filter
  tagMapping['timeOfDay'] = {
    'MORNING': 'Morning',
    'LUNCH': 'Lunch',
    'AFTERNOON': 'Afternoon',
  }

  if (locationData) {
    locationData.data.map(p => (tagMapping.postcodes[p.location] = p.location))
  }

  return tagMapping
}

/**
 * @typedef FilterState This is an object with any keys but the values of those
 * keys will be an array of strings. For example you may store the selected
 * brand ambassadors and selected weeks.
 * ```
 * {
 *   brandAmbassadors: [ '1', '2' ],
 *   weeks: [ '2019-W1', '2019-W2' ],
 * }
 * ```
 *
 * @typedef TagMapping This is a mapping object so tags which are just strings
 * can be resolved to values that are easier to deal with. It should be a hash
 * map of keys to another hashmap of values to tags. For example if we wanted
 * to resolve brand ambassador names to their ids we may have.
 * ```
 * {
 *  brandAmbassadors: {
 *    1: 'Sam',
 *    2: 'Sally'
 *  }
 * }
 * ```
 */

/**
 * Convert filter state to tags.
 * @param {FilterState} filterState
 * @param {TagMapping} tagMapping
 */
export const convertFilterStateToTags = (filterState, tagMapping) => {
  var tags = []
  Object.keys(filterState).forEach(mappingKey => {
    var values = filterState[mappingKey]
    values.forEach(val => {
      var tagMappingHashmap = tagMapping[mappingKey]
      if (!tagMappingHashmap)
        throw new Error(`Unrecognised mapping key '${mappingKey}'.`)

      var tag = tagMappingHashmap[val]
      if (!tag)
        throw new Error(
          `Unrecognised state with mapping key
          '${mappingKey}' and value '${val}'.`.replace(/\s+/g, ' '),
        )

      tags.push(tag)
    })
  })
  return tags
}

/**
 * Converts tags into a filter state object.
 * @param {Array<string>} tags
 * @param {TagMapping} tagMapping
 * @returns {FilterState}
 */
export const convertTagsToFilterState = (tags, tagMapping) => {
  var filterState = {}

  // Convert the tag mapping into a tag to value hashmap to make the next part
  // easier
  var tagsHashmap = {}
  Object.keys(tagMapping).forEach(key => {
    filterState[key] = []
    Object.keys(tagMapping[key]).forEach(value => {
      var tag = tagMapping[key][value]
      // If we have repeat hashmaps skip because we want to use the first
      // match
      if (tagsHashmap[tag]) return
      tagsHashmap[tag] = [key, value]
    })
  })

  tags.forEach(tag => {
    var match = tagsHashmap[tag]
    if (!match) return
    var [key, value] = match
    filterState[key].push(value)
  })

  return filterState
}

export default CityPlanFilterWrapper
